import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import { useState } from 'react'
import {
  getRequirementId,
  isBuiltInLocationValue,
  isRequirementRef,
  toRequirementRef,
} from '../../../../../util/namedLocationRequirement.util'
import { INPUT_POLICY_PATH, isNamedLocationInput } from '../../policy_feature/inputs'

const deriveInitialState = (policyBody) => {
  const autoModeInputs = []
  const requirementIds = {}

  Object.keys(INPUT_POLICY_PATH)
    .filter(isNamedLocationInput)
    .forEach((input) => {
      const values = get(policyBody, INPUT_POLICY_PATH[input]) || []
      // A built-in value (e.g. "All") sits alongside real requirement refs unwrapped — it needs no
      // definition and never resolves — so it's tracked here the same way, by its own literal value.
      const references = values
        .filter((value) => isRequirementRef(value) || isBuiltInLocationValue(value))
        .map((value) => (isRequirementRef(value) ? getRequirementId(value) : value))
      if (references.length) {
        autoModeInputs.push(input)
        requirementIds[input] = references
      }
    })

  return { autoModeInputs, requirementIds }
}

// Exposed so a caller can seed a field-visibility list with fields that already resolve via a
// stored requirement, without waiting a render for the hook itself to report it.
export const getAutoModeNamedLocationInputs = (policyBody) => deriveInitialState(policyBody).autoModeInputs

// A requirementId is an opaque reference key, generated once at definition time and never
// derived from the (editable) name — unlike a group requirement, whose name IS its reference,
// a named location's contents (and name) are authored directly on the template, so the name
// must stay editable without orphaning existing field attachments or DB bindings. Avoids
// crypto.randomUUID() — not polyfilled in every test environment this runs under.
export const generateNamedLocationRequirementId = () =>
  `nl_${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`

// Owns, for a CA template's named-location-bearing fields, whether each one is resolved
// automatically from one or more stored named location requirements or still manually prompted
// at deploy (the pre-existing, backward-compatible default); and owns the template's own
// requirement definitions (name, type, contents, trusted) — unlike a group requirement, a named
// location has no external template type to source its definition from, so the CA template
// itself is the definition's home.
export default function useNamedLocationFieldRequirements({
  initialPolicyBody,
  policyBody,
  setPolicyBody,
  definitions,
  setDefinitions,
}) {
  const [{ autoModeInputs, requirementIds }, setState] = useState(() => deriveInitialState(initialPolicyBody))

  const isAutoMode = (input) => autoModeInputs.includes(input)
  const getAttachedRequirementIds = (input) => requirementIds[input] || []

  const writeFieldValue = (input, values) => {
    const updated = cloneDeep(policyBody) || {}
    set(updated, INPUT_POLICY_PATH[input], values)
    setPolicyBody(updated)
  }

  const toggleAutoMode = (input, enabled) => {
    setState((current) => ({
      autoModeInputs: enabled
        ? [...current.autoModeInputs, input]
        : current.autoModeInputs.filter((key) => key !== input),
      requirementIds: enabled ? current.requirementIds : { ...current.requirementIds, [input]: [] },
    }))

    if (!enabled) {
      writeFieldValue(input, [])
    }
  }

  // The single source of truth for "which requirements are on this field" — matches the
  // MultiSelectPillList/MultiSelectSearch contract of handing back the whole new selection on
  // every change, add or remove alike.
  const setAttachedRequirementIds = (input, updatedIds) => {
    setState((current) => ({ ...current, requirementIds: { ...current.requirementIds, [input]: updatedIds } }))
    writeFieldValue(
      input,
      updatedIds.map((id) => (isBuiltInLocationValue(id) ? id : toRequirementRef(id))),
    )
  }

  const getDefinitions = () => definitions
  const getDefinition = (requirementId) => definitions.find((definition) => definition.requirementId === requirementId)

  // Adds a brand new requirement definition, or replaces an existing one in place (editing a
  // definition's name/type/contents must not change its requirementId — every field and DB
  // binding referencing it keys off that id, not the name).
  const upsertDefinition = (definition) => {
    setDefinitions((current) => {
      const index = current.findIndex((existing) => existing.requirementId === definition.requirementId)
      if (index === -1) {
        return [...current, definition]
      }
      const updated = [...current]
      updated[index] = definition
      return updated
    })
  }

  // Deletes a requirement definition from the template entirely, detaching it from every field
  // that referenced it — leaving a "location:<id>" pointing at a definition that no longer exists
  // would resolve as unresolvable (or worse, silently missing) at deploy time.
  const removeDefinition = (requirementId) => {
    setDefinitions((current) => current.filter((definition) => definition.requirementId !== requirementId))

    setState((current) => ({
      ...current,
      requirementIds: Object.fromEntries(
        Object.entries(current.requirementIds).map(([input, ids]) => [input, ids.filter((id) => id !== requirementId)]),
      ),
    }))

    const updated = cloneDeep(policyBody) || {}
    Object.keys(INPUT_POLICY_PATH)
      .filter(isNamedLocationInput)
      .forEach((input) => {
        const values = get(updated, INPUT_POLICY_PATH[input])
        if (Array.isArray(values)) {
          set(
            updated,
            INPUT_POLICY_PATH[input],
            values.filter((value) => getRequirementId(value) !== requirementId),
          )
        }
      })
    setPolicyBody(updated)
  }

  const hydrate = (nextPolicyBody) => {
    setState(deriveInitialState(nextPolicyBody))
  }

  return {
    isAutoMode,
    getAttachedRequirementIds,
    toggleAutoMode,
    setAttachedRequirementIds,
    hydrate,
    getDefinitions,
    getDefinition,
    upsertDefinition,
    removeDefinition,
  }
}
